import { useMemo, useState } from 'react';
import { BarChart, type BarDatum } from '@/components/charts/BarChart';
import { Card, CardHead } from '@/components/Card';
import { DataGrid, type ColumnDef } from '@/components/DataGrid';
import { DistRow } from '@/components/charts/DistRow';
import { EmptyState } from '@/components/EmptyState';
import { Icon } from '@/components/Icon';
import { Stat } from '@/components/Stat';
import { Status } from '@/components/Status';
import {
  useActorPerformance,
  useByCommune,
} from '@/hooks/useDashboards';
import type { ActorPerfRow, ByCommuneRow } from '@/api/dashboards';
import { fmt } from '@/lib/format';

type WindowChoice = '7' | '30' | '90';

/**
 * Operational + business dashboards for the back-office. Two cards on a
 * row:
 *
 *   • Performance des enrôleurs : top-N acteurs et leur split
 *     enrollments / calls / visits. Hides revoked actors per the memo.
 *   • Volumes par commune : distribution des événements sur la fenêtre,
 *     visualisée via le composant DistRow.
 *
 * A small segmented control switches the window between 7 / 30 / 90
 * days. Real-time stats (Prometheus) are left out for this slice — they
 * live on the Grafana stack.
 */
export function AdminDashboards() {
  const [windowDays, setWindowDays] = useState<WindowChoice>('30');
  const range = useMemo(() => buildRange(windowDays), [windowDays]);

  const actors = useActorPerformance(range);
  const communes = useByCommune(range);

  const actorRows = (actors.data ?? []).filter((a) => a.actorStatus !== 'REVOKED');
  const communeRows = communes.data ?? [];

  const actorsTotal = actorRows.reduce((s, a) => s + a.total, 0);
  const communesTotal = communeRows.reduce((s, c) => s + c.total, 0);
  const topActor = actorRows[0];

  return (
    <div>
      <div className="toolbar">
        <div className="seg">
          <button className={windowDays === '7' ? 'on' : ''} onClick={() => setWindowDays('7')}>
            7 jours
          </button>
          <button className={windowDays === '30' ? 'on' : ''} onClick={() => setWindowDays('30')}>
            30 jours
          </button>
          <button className={windowDays === '90' ? 'on' : ''} onClick={() => setWindowDays('90')}>
            90 jours
          </button>
        </div>
        <div className="muted" style={{ marginLeft: 'auto', fontSize: 12.5, fontWeight: 600 }}>
          Fenêtre : {range.from ?? '—'} → {range.to ?? '—'}
        </div>
      </div>

      <div className="grid grid-4" style={{ marginBottom: 18 }}>
        <Stat
          label="Événements (fenêtre)"
          value={fmt(actorsTotal)}
          delta={`${actorRows.length} enrôleurs actifs`}
          icon="activity"
          tint
        />
        <Stat
          label="Top enrôleur"
          value={topActor?.actorName ?? '—'}
          delta={topActor ? `${fmt(topActor.total)} événements` : ''}
          icon="user"
        />
        <Stat
          label="Communes couvertes"
          value={fmt(communeRows.length)}
          delta={`${fmt(communesTotal)} événements`}
          icon="pin"
        />
        <Stat
          label="Source dominante"
          value={dominantSource(communeRows)}
          delta="par type d'attribut"
          icon="layers"
        />
      </div>

      <div className="grid grid-2">
        <Card>
          <CardHead
            title="Performance des enrôleurs"
            subtitle="acteurs actifs uniquement, classés par total"
            right={
              <span className="meta">
                <Icon name="user" size={12} />
                {fmt(actorRows.length)} acteurs
              </span>
            }
          />
          <div className="scrollx">
            {actors.isLoading && (
              <div className="muted" style={{ padding: 24, textAlign: 'center' }}>Chargement…</div>
            )}
            {!actors.isLoading && actorRows.length === 0 && (
              <EmptyState icon="user" title="Aucun enrôleur actif" subtitle="Aucune activité sur la fenêtre sélectionnée." />
            )}
            {actorRows.length > 0 && <ActorTable rows={actorRows} />}
          </div>
        </Card>

        <Card>
          <CardHead
            title="Volumes par commune"
            subtitle="agrégés par type d'attribut sur la fenêtre"
            right={
              <span className="meta">
                <Icon name="pin" size={12} />
                {fmt(communeRows.length)} lignes
              </span>
            }
          />
          <div style={{ padding: '16px 22px' }}>
            {communes.isLoading && (
              <div className="muted" style={{ padding: 14, textAlign: 'center' }}>Chargement…</div>
            )}
            {!communes.isLoading && communeRows.length === 0 && (
              <EmptyState icon="pin" title="Pas de volumes" subtitle="Aucune commune n'a reçu d'événements sur la fenêtre." />
            )}
            {communeRows.length > 0 && <CommuneDistribution rows={communeRows} />}
          </div>
        </Card>
      </div>

      <Card style={{ marginTop: 18 }}>
        <CardHead
          title="Mix des actions par enrôleur"
          subtitle="enrollments / calls / visits sur la fenêtre"
        />
        <div style={{ padding: '16px 22px 22px' }}>
          {actorRows.length > 0 ? (
            <ActorMixChart rows={actorRows.slice(0, 8)} />
          ) : (
            <EmptyState icon="chart" title="Aucune donnée à tracer" subtitle="Ajustez la fenêtre temporelle pour élargir." />
          )}
        </div>
      </Card>
    </div>
  );
}

function ActorTable({ rows }: { rows: ActorPerfRow[] }) {
  const columns = useMemo<ColumnDef<ActorPerfRow>[]>(
    () => [
      {
        accessorKey: 'actorName',
        header: 'Acteur',
        size: 240,
        Cell: ({ row }) => (
          <div className="cell-stack">
            <span className="pri">{row.original.actorName}</span>
            <span className="s2" style={{ fontFamily: 'var(--mono)' }}>{row.original.actorId}</span>
          </div>
        ),
      },
      {
        accessorKey: 'actorStatus',
        header: 'Statut',
        size: 110,
        Cell: ({ cell }) => (
          <Status kind={cell.getValue<string>() === 'ACTIVE' ? 'ok' : 'mute'}>
            {cell.getValue<string>()}
          </Status>
        ),
      },
      {
        accessorKey: 'enrollments',
        header: 'Enrôlements',
        size: 120,
        mantineTableHeadCellProps: { align: 'right' },
        mantineTableBodyCellProps: { align: 'right' },
        Cell: ({ cell }) => (
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
            {fmt(cell.getValue<number>())}
          </span>
        ),
      },
      {
        accessorKey: 'calls',
        header: 'Appels',
        size: 100,
        mantineTableHeadCellProps: { align: 'right' },
        mantineTableBodyCellProps: { align: 'right' },
        Cell: ({ cell }) => (
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
            {fmt(cell.getValue<number>())}
          </span>
        ),
      },
      {
        accessorKey: 'visits',
        header: 'Visites',
        size: 100,
        mantineTableHeadCellProps: { align: 'right' },
        mantineTableBodyCellProps: { align: 'right' },
        Cell: ({ cell }) => (
          <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>
            {fmt(cell.getValue<number>())}
          </span>
        ),
      },
      {
        accessorKey: 'total',
        header: 'Total',
        size: 100,
        mantineTableHeadCellProps: { align: 'right' },
        mantineTableBodyCellProps: { align: 'right' },
        Cell: ({ cell }) => (
          <strong style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--ink)' }}>
            {fmt(cell.getValue<number>())}
          </strong>
        ),
      },
    ],
    [],
  );

  return (
    <DataGrid
      columns={columns}
      data={rows}
      disableGlobalFilter
      disableColumnFilters
      maxHeight={460}
      emptyMessage="Aucun acteur actif."
    />
  );
}

function CommuneDistribution({ rows }: { rows: ByCommuneRow[] }) {
  const aggregated = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) {
      m.set(r.commune, (m.get(r.commune) ?? 0) + r.total);
    }
    return Array.from(m.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10);
  }, [rows]);
  const grand = aggregated.reduce((s, [, n]) => s + n, 0);
  return (
    <>
      {aggregated.map(([commune, count], i) => (
        <DistRow
          key={commune}
          label={commune || '— commune inconnue'}
          count={count}
          total={grand}
          accent={i === 0}
        />
      ))}
    </>
  );
}

function ActorMixChart({ rows }: { rows: ActorPerfRow[] }) {
  const data: BarDatum[] = rows.map((r, i) => ({
    label: shorten(r.actorName, 14),
    value: r.total,
    highlight: i === 0,
    hint: `${r.actorName}: ${r.total.toLocaleString('fr-FR')} événements (e: ${r.enrollments}, a: ${r.calls}, v: ${r.visits})`,
  }));
  return <BarChart data={data} height={150} />;
}

function buildRange(window: WindowChoice): { from: string; to: string } {
  const to = new Date();
  const from = new Date(to.getTime() - Number(window) * 86_400_000);
  return { from: from.toISOString(), to: to.toISOString() };
}

function dominantSource(rows: ByCommuneRow[]): string {
  if (rows.length === 0) return '—';
  const m = new Map<string, number>();
  for (const r of rows) {
    m.set(r.attributeType, (m.get(r.attributeType) ?? 0) + r.total);
  }
  let best = ['—', 0] as [string, number];
  for (const e of m.entries()) {
    if (e[1] > best[1]) best = [e[0], e[1]];
  }
  return best[0];
}

function shorten(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max - 1) + '…';
}
